import React, { useEffect, useMemo, useState } from 'react'
import { Range } from 'react-date-range'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { RelevantDateForm } from 'src/pages/Evidence/modals/forms/RelevantDateForm'
import isFunction from 'lodash/isFunction'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useCurrentAudit } from 'src/components/async/model/audit'
import { stripFileExtension } from 'src/components/Reusable/Forms/FileSelector/FileSelector.helpers'
import { formatTimestamp } from 'src/Utils/formatDate'
import { isWebUri } from 'valid-url'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import {
  useHasReceptor,
  useReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import {
  AddEvidenceCaption,
  AddEvidenceDetails,
  FilesLoading,
} from './AddEvidenceForm.components'
import { AddEvidenceError, AddEvidenceFormBody } from './AddEvidenceForm.styles'
import {
  AddEvidenceErrors,
  EvidenceErrorInitState,
  EvidenceFormData,
  EvidenceFormDataInitState,
  EvidenceType,
  MAX_UPLOAD_MB,
} from './AddEvidenceForm.constants'

export const AddEvidenceForm = ({
  modalId,
  formId,
  onSubmitFunc,
  controlId,
  isShown,
  initialFile,
}: {
  modalId: ModalFormId | undefined
  formId: ModalFormId
  onSubmitFunc: (
    caption: string,
    formData: EvidenceFormData,
    formDataList: EvidenceFormData[],
    date: Timestamp,
    type: EvidenceType,
    uploadSize?: number,
  ) => Promise<void>
  controlId?: string
  isShown: boolean
  initialFile?: File
}): JSX.Element => {
  const { data: HasSPReceptor } = useHasReceptor('trr-sharepoint', true)
  const hasSharepointReceptor = useMemo(
    () => HasSPReceptor?.getHasReceptor() || false,
    [HasSPReceptor],
  )
  const { data: spReceptor } = useReceptor({
    modelId: 'trr-sharepoint',
    shouldFetch: hasSharepointReceptor,
  })

  const spCredentials = useMemo(() => {
    return JSON.parse(spReceptor?.getCredentials() || '{}')
  }, [spReceptor])

  const { filesSelected } = useSharepointFilePickerContext()
  const { filesSelected: gdriveFilesSelected } = useGoogleDriveContext()
  const { filesSelected: confluenceFilesSelected } =
    useConfluenceFilePickerContext()

  const { data } = useCurrentAudit()
  const [formData, formDataSet] = useState<EvidenceFormData>(
    EvidenceFormDataInitState,
  )
  const [formDataList, setFormDataList] = useState<EvidenceFormData[]>([])
  const [errors, setErrors] = useState<AddEvidenceErrors>(
    EvidenceErrorInitState,
  )
  const [caption, setCaption] = useState<string>('')
  const [type, typeSet] = useState<EvidenceType>(EvidenceType.NULL)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [range, setRange] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
  })
  const [uploadSize, setUploadSize] = useState<number>(0)

  const auditStart = useMemo(() => data?.getStartDate() || undefined, [data])
  const auditEnd = useMemo(() => data?.getEndDate() || undefined, [data])

  useEffect(() => {
    if (filesSelected.length) {
      if (filesSelected.length === 1) {
        setCaption(stripFileExtension(filesSelected[0].name) || '')
      } else {
        setCaption('')
      }
      return
    }

    if ((gdriveFilesSelected || []).length) {
      if ((gdriveFilesSelected || []).length === 1) {
        setCaption(
          stripFileExtension(gdriveFilesSelected?.[0]?.name ?? '') || '',
        )
      } else {
        setCaption('')
      }
      return
    }

    if ((confluenceFilesSelected || []).length) {
      if (confluenceFilesSelected.length === 1) {
        const n = confluenceFilesSelected[0]
        setCaption(stripFileExtension(n.fileName || n.title || '') || '')
      } else {
        setCaption('')
      }
    }
  }, [filesSelected, gdriveFilesSelected, confluenceFilesSelected])

  const clearFormData = () => {
    formDataSet(EvidenceFormDataInitState)
    setFormDataList([])
    setCaption('')
    setErrors(EvidenceErrorInitState)
    setRange({
      startDate: new Date(),
      endDate: new Date(),
    })
    setUploadSize(0)
  }

  useEffect(() => {
    if (!isShown) {
      clearFormData()
    }
  }, [isShown])

  useEffect(() => {
    if (initialFile) {
      updateEvidenceFiles(
        [{ mime: initialFile.type, body: initialFile }],
        EvidenceType.FILE,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile])

  useEffect(() => {
    const now = new Date()
    let endDate
    if (auditStart && auditEnd) {
      if (auditStart.toDate() > now) {
        endDate = auditStart.toDate()
      } else if (auditEnd.toDate() < now) {
        endDate = auditEnd.toDate()
      } else {
        endDate = now
      }
      setRange({
        startDate: endDate,
        endDate,
      })
    }
  }, [auditStart, auditEnd])

  const createCaption = (captionString: string) => {
    setCaption(captionString)
    setErrors((state) => {
      const newState = {
        ...state,
        captionError: '',
      }
      return newState
    })
  }

  const updateUploadSize = (totalSizeInMB: number) => {
    setUploadSize(totalSizeInMB)
  }

  const validateForm = () => {
    let isValid = true

    const hasSharepoint = filesSelected.length > 0
    const hasGDrive = (gdriveFilesSelected || []).length > 0
    const hasConfluence = (confluenceFilesSelected || []).length > 0

    if (
      !formDataList.length &&
      !formData.body &&
      !hasSharepoint &&
      !hasGDrive &&
      !hasConfluence
    ) {
      setErrors((state) => {
        const newState = {
          ...state,
          evidenceError: 'Please add evidence.',
        }
        return newState
      })
      isValid = false
    }

    if (
      (type !== EvidenceType.FILE || formDataList.length < 2) &&
      (filesSelected.length == 1 ||
        (gdriveFilesSelected || []).length == 1 ||
        confluenceFilesSelected.length == 1 ||
        type !== EvidenceType.NULL)
    ) {
      // If there is only one file, the caption is required
      // If there are multiple files, the caption is not required, the caption will be the file name
      if (!caption.trim().length) {
        setErrors((state) => {
          const newState = {
            ...state,
            captionError: 'Please enter a caption.',
          }
          return newState
        })
        isValid = false
      }
    }

    if (auditStart && auditEnd) {
      if (
        (auditEnd && range.endDate && range.endDate > auditEnd.toDate()) ||
        (auditStart && range.endDate && range.endDate < auditStart.toDate())
      ) {
        setErrors((state) => {
          const newState = {
            ...state,
            dateError: `Select a date within the range of your current audit so evidence is included in this audit. This audit's date range: ${formatTimestamp(
              auditStart,
            )} to ${formatTimestamp(auditEnd)}`,
          }
          return newState
        })
        isValid = false
      }
    }

    if (uploadSize > MAX_UPLOAD_MB) {
      setErrors((state) => {
        const newState = {
          ...state,
          evidenceError: 'Please ensure total size of upload is 100 MB or less',
        }
        return newState
      })
      isValid = false
    }

    if (formDataList.some((formData) => !formData.body)) {
      setErrors((state) => {
        const newState = {
          ...state,
          evidenceError: 'All files must have a body',
        }
        return newState
      })
      isValid = false
    }

    if (type === EvidenceType.LINK && !isWebUri(formData.body as string)) {
      isValid = false
    }

    if (
      type === EvidenceType.SHAREPOINTLINK &&
      !formData.body.toString().startsWith(spCredentials?.baseurl || 'https://')
    ) {
      isValid = false
    }

    return isValid
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const isValid = validateForm()
    if (!isValid) return
    setIsSubmitting(true)
    isFunction(onSubmitFunc) &&
      (await onSubmitFunc(
        caption,
        formData,
        formDataList,
        Timestamp.fromDate(range.endDate || new Date()),
        type,
        uploadSize,
      ))
    setIsSubmitting(false)
  }

  const updateEvidence = (mime: MIME_TYPE, type: EvidenceType) => {
    formDataSet({
      mime,
      body: '',
    })
    typeSet(type)
    setErrors((state) => {
      const newState = {
        ...state,
        evidenceError: '',
      }
      return newState
    })
  }

  const updateEvidenceFiles = (
    files: EvidenceFormData[],
    type: EvidenceType,
  ) => {
    if (files.length === 1) {
      if (files[0].body instanceof File && !caption.trim().length) {
        setCaption(files[0].body.name || '')
      }
    } else {
      files.forEach((file) => {
        if (file.body instanceof File) {
          file.caption = stripFileExtension(file.body.name)
        }
      })
    }
    setFormDataList(files)
    typeSet(type)
    setErrors((state) => {
      const newState = {
        ...state,
        evidenceError: '',
      }
      return newState
    })
  }

  return (
    <>
      {isSubmitting ? (
        <FilesLoading
          type={type}
          numFiles={formDataList.length}
          uploadSize={uploadSize}
        />
      ) : (
        <AddEvidenceFormBody id={formId} onSubmit={onSubmit}>
          <AddEvidenceCaption
            numFiles={
              formDataList.length ||
              filesSelected.length ||
              (gdriveFilesSelected || []).length ||
              confluenceFilesSelected.length
            }
            caption={caption}
            error={errors.captionError}
            isError={() => !!errors.captionError}
            createCaption={createCaption}
          />
          <br />
          {!filesSelected.length && (
            <RelevantDateForm
              range={range}
              setRange={setRange}
              errorSection={
                <AddEvidenceError showError={!!errors.dateError}>
                  {errors.dateError}
                </AddEvidenceError>
              }
              controlIds={controlId ? [controlId] : []}
            />
          )}
          <br />
          <AddEvidenceDetails
            type={type}
            formData={formData}
            formDataList={formDataList}
            updateEvidence={updateEvidence}
            updateEvidenceFiles={updateEvidenceFiles}
            updateUploadSize={updateUploadSize}
            formDataSet={formDataSet}
            errors={errors}
            modalId={modalId}
          />
        </AddEvidenceFormBody>
      )}
    </>
  )
}
